import { withNamespaces } from 'react-i18next';
import {
    Badge,
    Card,
    CardBody,
    CardTitle,
    Table,
} from 'reactstrap';

import {
    DDSStatuses,
    ShipmentPriorities,
} from 'src/modules/3rd-party-management/constants';
import DateUtils from 'src/services/utils/DateUtils';
import { STANDARD_DATE_FORMAT } from 'src/components/constants';

import useUpcomingShipmentsMissingDds from './hooks/useUpcomingShipmentsMissingDds';

const dateUtils = new DateUtils();

const UpcomingShipmentsMissingDdsSkeleton = () => (
    <Card className="h-100 mb-0">
        <CardBody>
            <div className="dt-field dt-skeleton mb-4" style={{ width: '45%', height: '24px' }} />
            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '220px' }} />
        </CardBody>
    </Card>
);

const DdsStatusBadge = ({
    t,
    status,
}) => {
    const config = DDSStatuses[status];

    if (!config) {
        return <span>—</span>;
    }

    return (
        <Badge className={`bg-${config.color || 'secondary'}`}>
            {config.icon || null}
            {t(config.title)}
        </Badge>
    );
};

const PriorityBadge = ({
    t,
    priority,
}) => {
    const config = ShipmentPriorities[priority];

    if (!config) {
        return <span>—</span>;
    }

    return (
        <Badge className={`bg-${config.color || 'secondary'}`}>
            {t(config.title)}
        </Badge>
    );
};

const UpcomingShipmentsMissingDds = ({
    t,
}) => {
    const {
        data: shipments,
        isLoading,
        isFetching,
        isError,
    } = useUpcomingShipmentsMissingDds({ t });

    if (isLoading || isFetching) {
        return <UpcomingShipmentsMissingDdsSkeleton />;
    }

    if (isError || !shipments) {
        return null;
    }

    return (
        <Card className="h-100 mb-0">
            <CardBody>
                <CardTitle tag="h6" className="mb-4 overview-section-title ">
                    {t('Upcoming Shipments Missing DDS')}
                </CardTitle>

                <div className="table-responsive">
                    {!shipments.length ? (
                        <div className="text-center py-4 text-muted">
                            {t('No upcoming shipments available.')}
                        </div>
                    ) : (
                        <Table className="mb-0" responsive borderless>
                            <thead>
                                <tr>
                                    <th>{t('Shipment ID')}</th>
                                    <th>{t('Product')}</th>
                                    <th>{t('Origin')}</th>
                                    <th>{t('Destination')}</th>
                                    <th>{t('DDS Status')}</th>
                                    <th>{t('Date')}</th>
                                    <th>{t('Priority')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shipments.map((shipment) => (
                                    <tr key={shipment.id}>
                                        <td>{shipment.shipmentId || '—'}</td>
                                        <td>{shipment.product || '—'}</td>
                                        <td>{shipment.origin || '—'}</td>
                                        <td>{shipment.destination || '—'}</td>
                                        <td>
                                            <DdsStatusBadge
                                                t={t}
                                                status={shipment.ddsStatus}
                                            />
                                        </td>
                                        <td>
                                            {shipment.date
                                                ? dateUtils.convertDateToDate(
                                                    shipment.date,
                                                    STANDARD_DATE_FORMAT
                                                )
                                                : '—'}
                                        </td>
                                        <td>
                                            <PriorityBadge
                                                t={t}
                                                priority={shipment.priority}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(UpcomingShipmentsMissingDds);
